import { useEffect, useRef, useState } from 'react';
import type { WsServerMessage } from '@sr-test-wizard/shared';

type Handler = (msg: WsServerMessage) => void;

export interface WsConnection {
  connected: boolean;
  send: (data: unknown) => void;
}

/**
 * Opens a single WebSocket to /ws and re-subscribes to `runId` whenever it
 * changes. Reconnects automatically with linear backoff when the socket
 * drops. The handler ref is updated without triggering reconnection.
 */
export function useWebSocket(runId: string | null, onMessage: Handler): WsConnection {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const open = () => {
      if (closed) return;
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${proto}//${location.host}/ws`);
      wsRef.current = ws;
      ws.onopen = () => {
        attempt = 0;
        setConnected(true);
        ws.send(JSON.stringify({ type: 'subscribe', runId }));
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WsServerMessage;
          handlerRef.current(msg);
        } catch {
          // ignore non-JSON frames
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (closed) return;
        attempt = Math.min(attempt + 1, 6);
        timer = setTimeout(open, attempt * 800);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          // already closing
        }
      };
    };

    open();
    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      wsRef.current?.close();
      setConnected(false);
    };
  }, [runId]);

  return {
    connected,
    send: (data: unknown) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(data));
    },
  };
}
